import type { ByDatabaseId } from "type_aliases";
import { OrganizationMember } from "../../../models/domain/organization_member";
import { ORGANIZATION_MEMBER_UPDATED, OrganizationMemberUpdatedEvent } from "../../../events/superadmin/organization_member_updated";
import { ORGANIZATION_MEMBERS_LOADED, OrganizationMembersLoadedEvent } from "../../../events/superadmin/organization_members_loaded";
import { ORGANIZATION_MEMBER_CREATED, OrganizationMemberCreatedEvent } from "../../../events/superadmin/organization_member_created";
import { ORGANIZATION_MEMBER_REMOVED, OrganizationMemberRemovedEvent } from "../../../events/superadmin/organization_member_removed";
import { produce } from "immer";

export type OrganizationMemberEvents = OrganizationMemberUpdatedEvent | OrganizationMembersLoadedEvent | OrganizationMemberCreatedEvent | OrganizationMemberRemovedEvent;

export type OrganizationMemberStore = {
  byFirebaseOrganizationId: {
    [firebaseId: string]: {
      membersByEmail: { [email: string]: OrganizationMember }
    }
  }
}

export function reduceOrganizationMembers(memberStore: OrganizationMemberStore = { byFirebaseOrganizationId: { } }, event: OrganizationMemberEvents) {
  return produce<OrganizationMemberStore>(memberStore, (memberStoreDraft) => {
    if (!memberStoreDraft.byFirebaseOrganizationId) {
      memberStoreDraft.byFirebaseOrganizationId = {}
    }

    switch (event.type) {
      case ORGANIZATION_MEMBER_UPDATED:
      case ORGANIZATION_MEMBER_CREATED: {
        const firebaseOrganizationId = event.payload.firebaseOrganizationId;
        const member = event.payload.member;
        const email = member.email;
        let membersByEmailDraft = memberStoreDraft.byFirebaseOrganizationId[firebaseOrganizationId];
        if (membersByEmailDraft == null) {
          membersByEmailDraft = { membersByEmail: {} };
          memberStoreDraft.byFirebaseOrganizationId[firebaseOrganizationId] = membersByEmailDraft;
        }

        membersByEmailDraft.membersByEmail[email] = new OrganizationMember(member);
        return memberStoreDraft;
      }
      case ORGANIZATION_MEMBERS_LOADED: {
        let firebaseOrganizationId = event.payload.firebaseOrganizationId;
        let membersByEmailDraft = memberStoreDraft.byFirebaseOrganizationId[firebaseOrganizationId];
        if (membersByEmailDraft == null) {
          membersByEmailDraft = { membersByEmail: {} };
          memberStoreDraft.byFirebaseOrganizationId[firebaseOrganizationId] = membersByEmailDraft;
        }

        event.payload.members.forEach((apiOrganizationMember) => {
          const member = new OrganizationMember(apiOrganizationMember);
          const email = member.email;
          membersByEmailDraft.membersByEmail[email] = member;
        });

        return memberStoreDraft;
      }
      case ORGANIZATION_MEMBER_REMOVED: {
        const { firebaseOrganizationId, memberId } = event.payload;
        const orgEntry = memberStoreDraft.byFirebaseOrganizationId[firebaseOrganizationId];
        if (orgEntry?.membersByEmail) {
          const emailToRemove = Object.keys(orgEntry.membersByEmail).find(
            email => orgEntry.membersByEmail[email].id === memberId
          );
          if (emailToRemove) {
            delete orgEntry.membersByEmail[emailToRemove];
          }
        }
        return memberStoreDraft;
      }
      default: {
        return memberStoreDraft;
      }
    }
  });
}